import {Component, Event, EventEmitter, h, Host, Prop, State} from '@stencil/core';

import {
  fsInitialState,
  initFilmstripImages,
  getListItemClass,
  handleSlideMove,
  handleSlideEnd,
  handleNavigationKeys,
  handleStartEvent,
  handleNextSlide,
  handlePrevSlide,
  handleTransitionOver,
  formatCaptionData,
  generateRandomId,
  setDimensionParams,
  handleSlideCancel,
  onResize,
  renderCaptionData,

} from './filmstrip-utils/utils';
import { htmlSanitizer } from '@utils/html-sanitizer';
import { handleLinkTracking } from '@utils/analytics';
import store from "@utils/store";
@Component({
  tag: 'vs-filmstrip',
  styleUrls: ['./_filmstrip.scss'],
  shadow: false,
})
export class VsFilmstrip {
  /**
   @shortDescription name of the filmstrip container
   @controlName name */
  @Prop() dataid: string;
  @Prop() name: string = 'filmstrip';

  /**
   @shortDescription Stringified Images Array containing image specific details
   @controlName filmstripImages */
  @Prop() filmstripImages: any;

  /**
   @shortDescription size of filmstrip images, small, large
   @controlName size */
  @Prop() size: string = 'small';

  /**
   @shortDescription Loop can be either true or false, based on that images can be scrolled infinitely
   @controlName loop */
  @Prop() loop: boolean = false;

  /**
   @shortDescription Aria label for Next Arrow
   @controlName ariaLabelRightArrow */
  @Prop() ariaLabelRightArrow: string = 'Next Slide';

  /**
   @shortDescription Aria label for Prev Arrow
   @controlName ariaLabelLeftArrow */
  @Prop() ariaLabelLeftArrow: string = 'Previous Slide';

  /**
   @shortDescription Remove Bottom Margin- Defaults to 30px
   @controlName removeBottomMargin */
  @Prop() removeBottomMargin?: string | boolean = 'true';
  @State() filmstripData: any;

  @State() stateObject: fsInitialState = {
    id: '',
    numItems: 0,
    activeElementIndex: 0,
    slideIndex: 0,
    liClass: '',
    touchstartX: 0,
    touchendX: 0,
    isRtl: false,
    captionData: [],
    slidesData: [],
    wrapperWidth: '',
    isNext: false,
    isPrev: false,
    imageGap: 1,
    transitionEffect: 'none',
    firstItemIndex: 0,
    lastItemIndex: 0,
    transitionDelay: false,
    fsDelay: false,
    isTransitionStarted: false,
    isSliding: false,
    startX: 0,
    x: 0,
    swiped: 0,
    prevArrowAriaDisabled: false,
    nextArrowAriaDisabled: false,
    visible: 2,
    mouseDown: false,
    isUsingKeyboard: false,
  };
  @State() id: number;
  @State() currentTransformWidth: any = 0;
  @State() showPrev: boolean = true;
  @State() showNext: boolean = true;
  @State() swiped: number = 0;
  private slideWidth: number = 600;
  @State() isNavigationHidden: boolean = true;
  @State() originalArray: any;
  private fsWrapper: any;
  private firstRender = true;
  @Prop() isCaptionDataAvailable: boolean = false;
  @State() loading: boolean = true;
  @State() error: any = '';
  @Prop() mode: string;
  @Event() dataRequest: EventEmitter<string>;
  componentWillLoad() {
    this.id = generateRandomId();
    if (this.mode === 'authoring') {
      // Emit an event to request data from parent vs-app-root
      this.dataRequest.emit();
    }
    // Subscribe to store changes
    store.subscribe(() => {
      const state = store.getState().data;
      this.loading = state.loading;
      this.error = state.error;

      if (state.data && state.data.page && state.data.page[this.dataid]) {
        this.filmstripData = state.data.page[this.dataid];

        // Only proceed if filmstripImages exists
        if (this.filmstripData && this.filmstripData.filmstripImages) {
          const filmstripImages = this.filmstripData.filmstripImages;

          // Update component state
          this.stateObject.numItems = filmstripImages.length;
          this.stateObject.slidesData = filmstripImages;
          this.originalArray = filmstripImages;

          if(this.filmstripData.removeBottomMargin){
            this.removeBottomMargin = this.filmstripData.removeBottomMargin;
          }

          // Initialize filmstrip images
          initFilmstripImages(this);
          this.stateObject.captionData = formatCaptionData(this);

          if (!this.loop) {
            this.showPrev = false;
          }
          this.showNext = this.stateObject.numItems > this.stateObject.visible;
        }
      }
      this.loading=false;
    });

    // Set RTL status
    this.stateObject.isRtl = document.documentElement.getAttribute('dir') === 'rtl';

    // Get initial store state to check if data is already available
    const currentState = store.getState().data;
    if (currentState.data && currentState.data.page && currentState.data.page[this.dataid]) {
      this.filmstripData = currentState.data.page[this.dataid];

      if (this.filmstripData && this.filmstripData.filmstripImages) {
        this.stateObject.numItems = this.filmstripData.filmstripImages.length;
        this.stateObject.slidesData = this.filmstripData.filmstripImages;
        this.originalArray = this.filmstripData.filmstripImages;
        initFilmstripImages(this);

        if (!this.loop) {
          this.showPrev = false;
        }
        this.showNext = this.stateObject.numItems > this.stateObject.visible;
      }
    }
  }

  componentDidLoad() {
    const captionD = formatCaptionData(this);
    this.stateObject.captionData = captionD;
    this.stateObject.liClass = getListItemClass(this.stateObject.visible, this.size);
    this.initListItems(this.stateObject.visible);

    if (this.fsWrapper) {
      this.initListItems(this.stateObject.visible);
    }

    // Wait for images to load and measure
    const initializeFilmstrip = () => {
      const filmstripItem = this.fsWrapper.querySelector('vs-filmstrip-item');
      const firstLi = filmstripItem?.querySelector('li');
      const img = firstLi?.querySelector('img');

      if (firstLi && img) {
        const loadImage = () => {
          const width = firstLi.getBoundingClientRect().width;

          if (width > 10) {
            this.slideWidth = width;

            if (this.loop) {
              // Set initial transform to show the first non-cloned slide
              this.currentTransformWidth = this.stateObject.firstItemIndex * this.slideWidth;
              this.showPrev = true; // Enable previous button for infinite loop
            } else {
              this.currentTransformWidth = 0;
              this.showPrev = false; // Disable previous button at start for non-loop
            }

            this.stateObject = {
              ...this.stateObject,
              wrapperWidth: this.fsWrapper.offsetWidth,
              transitionEffect: 'none'
            };
          }
        };

        if (img.complete) {
          loadImage();
        } else {
          img.addEventListener('load', loadImage);
        }
      }
    };

    // Try initialization
    setTimeout(initializeFilmstrip, 100);

    // Add resize listener
    window.addEventListener('resize', e => onResize(e, this));

  }
  private updateTransformWidth = (width: number)=>{
    this.currentTransformWidth = width;
  }

  // START GENAI@CHATGPT4
  componentDidUpdate() {
    if (this.firstRender) {
      this.init();
      this.firstRender = false;
    }
  }
  // END GENAI@CHATGPT4

  private init() {
    if (this.fsWrapper) {
      this.initListItems(this.stateObject.visible);
      setDimensionParams(this);
    }
  }

  private initListItems(val) {
    if (this.fsWrapper) {
      this.stateObject.liClass = getListItemClass(val, this.size);
      const liItems = this.fsWrapper.querySelectorAll('li');
      liItems.forEach(li => {
        li.classList.add(this.stateObject.liClass);
      });
    }
  }
  private getParentComponentRef = () => {
    return this;
  };


  private keyBoardControlHandler = (e: KeyboardEvent) => {
    handleNavigationKeys(e, this);
  };
  private handleSlideStart = (e: MouseEvent | TouchEvent) => {
    handleStartEvent(e, this);
    this.stateObject.mouseDown = true;
  };
  private handleSlideMove = (e: MouseEvent | TouchEvent) => {
    e.preventDefault();
    handleSlideMove(e, this);
  };
  private handleSlideCancel = (e: MouseEvent | TouchEvent) => {
    e.preventDefault();
    handleSlideCancel(e, this);
  };
  private handleSlideEnd = (e: MouseEvent | TouchEvent) => {
    e.preventDefault();
    handleSlideEnd(e, this);
  };

  private handleMouseOver = () => {
    this.isNavigationHidden = false;
  };

  private handleMouseOut = () => {
    this.isNavigationHidden = true;
  };

  private handleRightArrowClick = () => {
    this.stateObject.isRtl ? this.prev() : this.next();
  };
  private handleLeftArrowClick = () => {
    this.stateObject.isRtl ? this.next() : this.prev();
  };
  private prev = (isKeyPressed = false) => {
    this.stateObject.isUsingKeyboard = isKeyPressed;
    handlePrevSlide(this, this.updateTransformWidth);
  };
  private next = (isKeyPressed = false) => {
    this.stateObject.isUsingKeyboard = isKeyPressed;
    handleNextSlide(this, this.updateTransformWidth);
  };
  private handleTransitionStart = () => {
    this.stateObject.isTransitionStarted = true;
    this.stateObject.transitionEffect = 'ease';
  };
  private handleTransitionEnd = () => {
    handleTransitionOver(this);
  };
  private handleMouseUp = e => {
    handleSlideEnd(e, this);
    this.stateObject.mouseDown = false;
  };

  private handleTransitionClass() {
    let fsWrapperClass = 'vs-fs-wrapper';
    if (this.stateObject.transitionEffect !== 'none') {
      fsWrapperClass += '  fs-transition';
    }
    return fsWrapperClass;
  }

  private getCaptionStyle = () => {
    return { width: this.slideWidth - 20 + 'px' };
  };

  private renderCaption = () => {
    return renderCaptionData(this);
  };

  private handleLinkClick = e => {
    if (e.target.nodeName === 'A') {
      const trackingData = {
        title: e.target.innerText,
        url: e.target.href,
        conversionType: e.target.dataset.conversiontype || 'NA',
        location: 'filmstrip',
      };
      handleLinkTracking({ ...trackingData });
    }
  };

  private renderSkeleton() {
    return (
      <div class="vs-filmstrip-skeleton">
        <div class="vs-fs" role="region">
          <div class="vs-fs-skeleton-wrapper">
            <div class="vs-fs-skeleton-content">
              {/* Generate appropriate number of slides based on size */}
              {Array(this.size === 'large' ? 3 : 4).fill(0).map((_, i) => (
                <div
                  key={`item-${i}`}
                  class={`vs-fs-skeleton-item ${this.size === 'large' ? 'vs-img-large' : 'vs-img-small'}`}
                >
                  <div class={`vs-fs-skeleton-image ${this.size === 'large' ? 'vs-img-large' : 'vs-img-small'}`}></div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation controls */}
          <div class="vs-fs-skeleton-btn vs-prev">
            <span></span>
          </div>
          <div class="vs-fs-skeleton-btn vs-next">
            <span></span>
          </div>
        </div>

        {/* Caption area */}
        <div class="vs-fs-skeleton-caption">
          <div class="vs-fs-skeleton-caption-line"></div>
          <div class="vs-fs-skeleton-caption-line"></div>
        </div>

        {/* Bottom margin if needed */}
        <div class={`${this.removeBottomMargin === "false" || this.removeBottomMargin === "" ? 'vs-mb-3' : 'vs-d-none'}`}></div>
      </div>
    );
  }

  render() {
    const isConfigured = !!this.dataid;
    if (this.loading && isConfigured) {
      return (
        <Host>
          {this.renderSkeleton()}
        </Host>
      );
    }
    return (
      <Host>
        <div onMouseOver={() => this.handleMouseOver()} onMouseOut={() => this.handleMouseOut()} onMouseLeave={e => handleSlideCancel(e, this)}>
          <div id={`filmstrip-${this.id}`} class="vs-fs" role="region" onMouseLeave={e => handleSlideCancel(e, this)}>
            <div
              ref={el => (this.fsWrapper = el)}
              style={{ transform: `translate3d(${this.stateObject.isRtl ? '' : '-'}${this.currentTransformWidth - this.swiped}px,0px,0px)` }}
              class={this.handleTransitionClass()}
              onTransitionEnd={this.handleTransitionEnd}
              onTransitionStart={this.handleTransitionStart}
              onKeyUp={this.keyBoardControlHandler}
              onTouchMove={this.handleSlideMove}
              onMouseMove={this.handleSlideMove}
              onTouchCancel={this.handleSlideCancel}
              onTouchEnd={this.handleSlideEnd}
              onMouseDown={this.handleSlideStart}
              onTouchStart={this.handleSlideStart}
              onMouseUp={this.handleMouseUp}
            >
              <vs-filmstrip-item
                li-class={this.stateObject.liClass}
                slideArr={this.stateObject.slidesData}
                size={this.size}
                num-items={this.stateObject.numItems}
                name={this.name}
                parentFilmstripRef={this.getParentComponentRef()}
              />
            </div>
            <vs-filmstrip-navigation
              parentFilmstripRef={this.getParentComponentRef()}
              showNext={this.showNext}
              showPrev={this.showPrev}
              onLeftArrowClick={this.handleLeftArrowClick}
              onRightArrowClick={this.handleRightArrowClick}
              isNavigationHidden={this.isNavigationHidden}
            />
          </div>

          <div dir={this.stateObject.isRtl ? 'rtl' : 'ltr'} class={`fs-image-description vs-text vs-meta ${!this.isCaptionDataAvailable ? 'vs-d-none' : ''}`}>
            <div
              id={`image-caption-${this.stateObject.activeElementIndex}`}
              style={this.getCaptionStyle()}
              onClick={this.handleLinkClick}
              innerHTML={htmlSanitizer(this.renderCaption())}
            />
          </div>
          <div class={`${this.removeBottomMargin=="true" ? 'vs-d-none' : 'vs-mb-3'}`}></div>
        </div>
      </Host>
    );
  }
}
